 
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Users, TrendingUp, Calendar, Star } from 'lucide-react'

const headerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
}

interface SavedHeaderProps {
  totalInfluencers?: number
  totalFollowers?: number
  averageEngagement?: number // Actually represents average analysis days now
  lastAdded?: string
}

export function SavedHeader({ 
  totalInfluencers = 0, 
  totalFollowers = 0, 
  averageEngagement = 0,
  lastAdded 
}: SavedHeaderProps) {
  const formatFollowers = (count: number) => {
    if (count >= 1000000000) return `${(count / 1000000000).toFixed(1)}B`
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`
    return count.toString()
  }

  const formatAnalysisDays = (days: number) => {
    return `${Math.round(days)}d`
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <Card className="relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-purple-400/10 to-transparent dark:from-purple-400/5 rounded-full -translate-y-8 translate-x-8" />
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <CardTitle className="text-2xl font-bold tracking-tight flex items-center gap-2">
                <Star className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                Saved Accounts
              </CardTitle>
              <p className="text-muted-foreground">Your curated list of creators and accounts</p>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Stats Overview - Only show if there are influencers */}
      {totalInfluencers > 0 && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card className="relative overflow-hidden">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-purple-400/20 to-transparent rounded-full -translate-y-4 translate-x-4" />
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Accounts</CardTitle>
              <Users className="h-5 w-5 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-500">{totalInfluencers}</div>
              <div className="flex items-center gap-1 mt-2">
                <div className="w-2 h-2 rounded-full bg-purple-500" />
                <p className="text-xs text-purple-600">In your collection</p>
              </div>
            </CardContent>
          </Card>

          <Card className="relative overflow-hidden">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-blue-400/20 to-transparent rounded-full -translate-y-4 translate-x-4" />
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Reach</CardTitle>
              <TrendingUp className="h-5 w-5 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-500">{formatFollowers(totalFollowers)}</div>
              <div className="flex items-center gap-1 mt-2">
                <div className="w-2 h-2 rounded-full bg-blue-500" />
                <p className="text-xs text-blue-600">Combined followers</p>
              </div>
            </CardContent>
          </Card>

          <Card className="relative overflow-hidden">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-green-400/20 to-transparent rounded-full -translate-y-4 translate-x-4" />
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Analysis Days</CardTitle>
              <Star className="h-5 w-5 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-500">{averageEngagement > 0 ? formatAnalysisDays(averageEngagement) : 'N/A'}</div>
              <div className="flex items-center gap-1 mt-2">
                <div className="w-2 h-2 rounded-full bg-green-500" />
                <p className="text-xs text-green-600">Average period</p>
              </div>
            </CardContent>
          </Card>

          <Card className="relative overflow-hidden">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-amber-400/20 to-transparent rounded-full -translate-y-4 translate-x-4" />
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Last Added</CardTitle>
              <Calendar className="h-5 w-5 text-amber-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-amber-500">{lastAdded ? new Date(lastAdded).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'N/A'}</div>
              <div className="flex items-center gap-1 mt-2">
                <div className="w-2 h-2 rounded-full bg-amber-500" />
                <p className="text-xs text-amber-600">Most recent</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
} 